/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { graphUrlLike } from "../../utils/graph-url-like.js";
import { createGraphNodeType } from "./kits.js";
import { VirtualNode } from "./virtual-node.js";
import { err } from "../../data/file-system/utils.js";
import { baseURLFromString, SENTINEL_BASE_URL } from "../../loader/loader.js";
import { getModuleId, isModule } from "../utils.js";
import { Template } from "../../utils/template.js";
import { isLLMContent, isLLMContentArray } from "../../data/common.js";
import { InspectableAssetImpl } from "./inspectable-asset.js";
export { GraphQueries };
function scanConfiguration(config, scanner) {
    for (const [, portValue] of Object.entries(config)) {
        let contents = null;
        if (isLLMContent(portValue)) {
            contents = [portValue];
        }
        else if (isLLMContentArray(portValue)) {
            contents = portValue;
        }
        if (!contents)
            continue;
        for (const content of contents) {
            for (const part of content.parts) {
                if ("text" in part) {
                    const template = new Template(part.text);
                    if (template.hasPlaceholders) {
                        template.transform((part) => {
                            scanner(part);
                            return part;
                        });
                    }
                }
            }
        }
    }
}
/**
 * Encapsulates common graph operations.
 */
class GraphQueries {
    #mutable;
    #graphId;
    constructor(cache, graphId) {
        this.#mutable = cache;
        this.#graphId = graphId;
    }
    #graph() {
        const graph = this.#mutable.graph;
        return this.#graphId ? graph.graphs[this.#graphId] : graph;
    }
    isEntry(id) {
        return this.incoming(id).length === 0;
    }
    isExit(id) {
        return this.outgoing(id).length === 0;
    }
    incoming(id) {
        return this.#graph()
            .edges.filter((edge) => edge.to === id)
            .map((edge) => this.#mutable.edges.getOrCreate(edge, this.#graphId));
    }
    outgoing(id) {
        return this.#graph()
            .edges.filter((edge) => edge.from === id)
            .map((edge) => this.#mutable.edges.getOrCreate(edge, this.#graphId));
    }
    entries() {
        return this.#mutable.nodes
            .nodes(this.#graphId)
            .filter((node) => node.isEntry());
    }
    nodeById(id) {
        if (this.#graph().virtual) {
            return new VirtualNode({ id });
        }
        return this.#mutable.nodes.get(id, this.#graphId);
    }
    typeForNode(id) {
        const node = this.nodeById(id);
        if (!node) {
            return undefined;
        }
        return this.typeById(node.descriptor.type);
    }
    typeById(id) {
        const knownNodeType = this.#mutable.kits.getType(id);
        if (knownNodeType) {
            return knownNodeType;
        }
        if (!graphUrlLike(id)) {
            return undefined;
        }
        return createGraphNodeType(id, this.#mutable);
    }
    moduleExports() {
        const exports = this.#mutable.graph.exports;
        if (!exports)
            return new Set();
        return new Set(exports.filter((e) => isModule(e)).map((e) => getModuleId(e)));
    }
    graphExports() {
        const exports = this.#mutable.graph.exports;
        if (!exports)
            return new Set();
        return new Set(exports.filter((e) => !isModule(e)).map((e) => e.slice(1)));
    }
    async imports() {
        if (this.#graphId || !this.#mutable.graph.imports)
            return new Map();
        const results = new Map();
        const entries = Object.entries(this.#mutable.graph.imports);
        for (const [name, value] of entries) {
            let outcome = err(`Unknown error resolving import "${name}`);
            if (!value || !("url" in value)) {
                outcome = err(`Invalid import value "${JSON.stringify(value)}`);
            }
            else {
                try {
                    const url = new URL(value.url, baseURLFromString(this.#mutable.graph.url) || SENTINEL_BASE_URL).href;
                    const store = this.#mutable.store;
                    const adding = store.addByURL(url, [this.#mutable.id], {});
                    const mutable = await store.getLatest(adding.mutable);
                    const inspectable = store.inspect(mutable.id, "");
                    if (!inspectable) {
                        outcome = err(`Unable to inspect graph at URL "${url}`);
                    }
                    else {
                        outcome = inspectable;
                    }
                }
                catch (e) {
                    outcome = err(e.message);
                }
                finally {
                    results.set(name, outcome);
                }
            }
        }
        return results;
    }
    assets() {
        const entries = Object.entries(this.#mutable.graph.assets || []);
        return new Map(entries.map(([path, asset]) => {
            return [path, new InspectableAssetImpl(path, asset)];
        }));
    }
    assetEdges() {
        const edges = [];
        const errors = [];
        for (const node of this.#mutable.nodes.nodes(this.#graphId)) {
            scanConfiguration(node.configuration(), (part) => {
                if (part.type !== "asset")
                    return;
                const { path, invalid } = part;
                if (invalid)
                    return;
                const asset = this.#mutable.graph.assets?.[path];
                if (!asset) {
                    errors.push(`Node with id "${node.descriptor.id}" ("${node.title()}") refers to non-existent asset "${path}"`);
                    return;
                }
                edges.push({
                    direction: "load",
                    asset: new InspectableAssetImpl(path, asset),
                    node: node,
                });
            });
        }
        if (errors.length > 0) {
            return err(errors.join("\n"));
        }
        return edges;
    }
}
//# sourceMappingURL=graph-queries.js.map